
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import CREATE_USER_EXPERIENCE from "@/apollo/profile/experiences/CreateExperience.gql";
import UPDATE_USER_EXPERIENCE from "@/apollo/profile/experiences/UpdateExperience.gql";
import REMOVE_EXPERIENCE from "@/apollo/profile/experiences/RemoveExperience.gql";

import ModalFullScreen from "@/components/modal/ModalFullScreen.vue";
import ModalHeader from "@/components/modal/ModalHeader.vue";
import FormDatepicker from "@/components/form/FormDatepickerNew.vue";
import FormGroup from "@/components/form/FormGroupNew.vue";
import FormTextarea from "@/components/form/FormTextarea.vue";
import SkillsMultiselect from "@/components/autocompleteMultiselects/SkillsMultiselectNew.vue";
import IndustriesMultiselect from "@/components/autocompleteMultiselects/IndustriesMultiselectNew.vue";
import ExperienceEmploymentTypeSelect from "@/components/autocompleteMultiselects/ExperienceEmploymentTypeSelect.vue";
import UIButton from "@/components/button/ui-button.vue";

import {
    Experience_Skill,
    Industry,
    User_Experience,
} from "@/generated/graphql";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";

@Component({
    components: {
        ModalFullScreen,
        ModalHeader,
        FormDatepicker,
        FormGroup,
        FormTextarea,
        SkillsMultiselect,
        IndustriesMultiselect,
        ExperienceEmploymentTypeSelect,
        UIButton,
        LoadingOverlay
    },
})
export default class ExperienceForm extends Vue {
    @Prop({ type: Object, default: undefined }) readonly experience?: User_Experience;
    @Prop({ type: String, required: true }) readonly userId!: string;
    @Prop({ type: Boolean, default: false }) readonly showDialog!: boolean;
    @Prop({ type: String, required: true}) readonly currentUserId!: string;

    id?: string = undefined;
    companyName: string = "";
    jobTitle: string = "";
    skillsIds: number[] = [];
    industry: Industry[] = [];
    summary: string = "";
    startDate: string | null = null;
    endDate: string | null = null;
    location: string = "";
    employmentType: string = "Full Time";
    ongoing: boolean = false;

    @Watch("experience", { immediate: true })
    watchProfile(experience): void {
        this.id = experience?.id;
        this.companyName = experience?.company_name ?? "";
        this.jobTitle = experience?.job_title ?? "";
        this.industry = experience?.industry_id ? experience?.industry_id.map(item => ({ id: item.id, name: item.name })) : [];
        this.summary = experience?.summary ?? "";
        this.startDate = experience?.start_date ? experience?.start_date : null;
        this.endDate = experience?.end_date ? experience?.end_date : null;
        this.location = experience?.location ?? "";
        this.employmentType = experience?.employment_type ?? "Full Time";
        this.ongoing = Boolean(experience?.ongoing);
        if (Array.isArray(experience?.skills)) {
        // If it's an array, map its values
        this.skillsIds = experience.skills.map((skill) => skill.id) ?? [];
    } else {
        // If it's not an array (likely an object), set skillsIds to an empty array
        this.skillsIds = [];
    }

    }

    get modalTitle(): string {
        const prefix = this.isEditing ? "Edit" : "Add";
        return `${prefix} Work Experience`;
    }

    get isEditing(): boolean {
        return Boolean(this.experience);
    }

    get experienceIndustryId() {
        return this.industry;
    }

    get experienceSkills(): Partial<Experience_Skill>[] {
        return this.skillsIds.map(skillId => ({
            id: skillId,
        }));
    }

    get experienceSet() {
        return {
            user_id: this.userId,
            job_title: this.jobTitle,
            company_name: this.companyName,
            employment_type: this.employmentType,
            ongoing: this.ongoing,
            location: this.location,
            summary: this.summary,
            start_date: this.startDate,
            skills: this.experienceSkills,
            end_date: this.ongoing ? null : this.endDate,
            industry_id: this.experienceIndustryId,
            added_by_id: this.currentUserId
        };
    }

    closeModal(): void {
        this.$emit("close", false);
    }

    async submit(): Promise<void> {
        try {
            if (this.isEditing) {
                await this.update();
            } else {
                await this.create();
            }
        } catch (error) {
            var msg = "";
            if (error instanceof Error) {
                msg = error.message;
            } else {
                msg = error as string;
            }
            this.$eventBus.emit(
                NOTIFICATION,
                Message.errorMessage(msg)
            );
        } finally {
            this.closeModal();
        }
    }

    async create(): Promise<void> {
        this.$emit("added", this.experienceSet);
    }

    async update(): Promise<void> {
        this.$emit("updated",this.experienceSet);
    }
}
